import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as services from "../services/performApi";
import { generateRandomString } from "utils/helper";
import { setGradeConfig, setIsLoading, setPerformList } from "../slices/attendanceSlice";
import { message, Modal } from "antd";
import { useTranslation } from "react-i18next";

function toInitialValues(performList = [], gradeConfig = {}) {
  const values = {};

  performList.forEach(item => {
    const { type, weight, statusFlag, gradeConfigs = [] } = item.performConfigs || {};
    const enabled = statusFlag === "Y";

    const sortedGradeConfigs = [...gradeConfigs].sort((a, b) => a.index - b.index);

    switch (type) {
      case "LATE":
        values.percentageOfLate = weight;
        values.gradeByNumberOfLate = enabled;
        sortedGradeConfigs.forEach(cfg => {
          values[`grade${cfg.grade}NoMoreLateThan`] = cfg.unit;
        });
        break;

      case "ABSENT":
        values.percentageOfAbsences = weight;
        values.gradeByNumberOfAbsences = enabled;
        sortedGradeConfigs.forEach(cfg => {
          values[`grade${cfg.grade}NoMoreAbsencesThan`] = cfg.unit;
        });
        break;

      case "LEAVE":
        values.percentageOfLeaves = weight;
        values.gradeByNumberOfLeaves = enabled;
        sortedGradeConfigs.forEach(cfg => {
          values[`grade${cfg.grade}NoMoreLeavesThan`] = cfg.unit;
        });
        break;

      default:
        break;
    }
  });

  const sortedGradeConfig = [...(gradeConfig?.gradeConfigs || [])]?.sort((a, b) => a.index - b.index);
  sortedGradeConfig?.forEach(cfg => {
    values[`grade${cfg.grade}NotLessThan`] = cfg.unit;
  });

  return values;
}

function useBesmartcondition() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { currentCompany, currentEmployee } = useSelector((state) => state.company);
  const { gradeConfig, performList } = useSelector((state) => state.attendance);

  const getGradeConfig = useCallback(async () => {
    try {
      dispatch(setIsLoading(true));
      const response = await services.getStpPerformGradeConfig({
        requestId: generateRandomString(),
        companyId: currentCompany,
        employeeId: currentEmployee
      });
      dispatch(setGradeConfig(response.data?.content));
    } catch (error) {
      message.error(t("common.error"))
    } finally {
      dispatch(setIsLoading(false));
    }
  }, [currentCompany, currentEmployee, dispatch]);

  const getPerformList = useCallback(async () => {
    try {
      dispatch(setIsLoading(true));
      const response = await services.getStpPerformList({
        requestId: generateRandomString(),
        companyId: currentCompany,
        employeeId: currentEmployee
      });
      dispatch(setPerformList(response.data?.content));
    } catch (error) {
      message.error(t("common.error"))
    } finally {
      dispatch(setIsLoading(false));
    }
  }, [currentCompany, currentEmployee, dispatch]);

  useEffect(() => {
    if (currentCompany) {
      getGradeConfig();
      getPerformList();
    }
  }, [currentCompany]);

  const initialValues = useMemo(() => {
    return toInitialValues(performList, gradeConfig);
  }, [performList, gradeConfig]);

  const editPerformGradeConfig = useCallback(
    async (values, gradeConfigId) => {
      try {
        dispatch(setIsLoading(true));
        const gradeConfigs = [
          { grade: "A", unit: Number(values?.gradeANotLessThan), index: 0 },
          { grade: "B", unit: Number(values?.gradeBNotLessThan), index: 1 },
          { grade: "C", unit: Number(values?.gradeCNotLessThan), index: 2 },
          { grade: "D", unit: Number(values?.gradeDNotLessThan), index: 3 },
          { grade: "F", unit: Number(values?.gradeFNotLessThan), index: 4 },
        ];

        const result = await services.editStpPerformGradeConfig({
          companyId: currentCompany,
          employeeId: currentEmployee,
          stpPerformGradeConfigId: gradeConfigId,
          gradeConfigs
        });
        message.success(t("common.success"))
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, currentEmployee, dispatch, gradeConfig]
  );

  const buildGradeConfigs = (values, type) => {
    const mapping = {
      LEAVE: {
        A: values?.gradeANoMoreLeavesThan,
        B: values?.gradeBNoMoreLeavesThan,
        C: values?.gradeCNoMoreLeavesThan,
        D: values?.gradeDNoMoreLeavesThan,
        F: values?.gradeFNoMoreLeavesThan,
      },
      ABSENT: {
        A: values?.gradeANoMoreAbsencesThan,
        B: values?.gradeBNoMoreAbsencesThan,
        C: values?.gradeCNoMoreAbsencesThan,
        D: values?.gradeDNoMoreAbsencesThan,
        F: values?.gradeFNoMoreAbsencesThan,
      },
      LATE: {
        A: values?.gradeANoMoreLateThan,
        B: values?.gradeBNoMoreLateThan,
        C: values?.gradeCNoMoreLateThan,
        D: values?.gradeDNoMoreLateThan,
        F: values?.gradeFNoMoreLateThan,
      },
    };

    return Object.entries(mapping[type])?.map(([grade, unit], index) => ({
      grade,
      unit: Number(unit),
      index,
    }));
  };

  const editPerformList = useCallback(
    async (values) => {
      try {
        dispatch(setIsLoading(true));

        const findIdByType = (type) =>
          performList?.find((item) => item.performConfigs?.type === type)?.stpPerformId;

        let req = {
          companyId: currentCompany,
          employeeId: currentEmployee,
          performList: [
            {
              type: "LEAVE",
              weight: values.percentageOfLeaves,
              statusFlag: values.gradeByNumberOfLeaves ? "Y" : "N",
              gradeConfigs: buildGradeConfigs(values, "LEAVE"),
              stpPerformId: findIdByType("LEAVE"),
            },
            {
              type: "ABSENT",
              weight: values.percentageOfAbsences,
              statusFlag: values.gradeByNumberOfAbsences ? "Y" : "N",
              gradeConfigs: buildGradeConfigs(values, "ABSENT"),
              stpPerformId: findIdByType("ABSENT"),

            },
            {
              type: "LATE",
              weight: values.percentageOfLate,
              statusFlag: values.gradeByNumberOfLate ? "Y" : "N",
              gradeConfigs: buildGradeConfigs(values, "LATE"),
              stpPerformId: findIdByType("LATE"),
            },
          ],
        }

        const result = await services.editStpPerformList(req);
        message.success(t("common.success"))
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, currentEmployee, dispatch, performList]
  );

  const onSubmitGradeConfig = useCallback(
    (values) => {
      return new Promise((resolve) => {
        Modal.confirm({
          title: t("dialog.confirmation.header"),
          content: (
            <span>
              {t("dialog.confirmation.edit_action")}
            </span>
          ),
          async onOk() {
            try {
              await editPerformGradeConfig(values, gradeConfig?.id);
              await editPerformList(values);

              await getGradeConfig();
              await getPerformList();

              resolve(true);
            } catch (error) {
              console.error("Submit error:", error);
              resolve(false);
            }
          },
          okText: t('common.confirm'),
          cancelText: t('common.cancel'),
          okButtonProps: { style: { height: "40px", borderRadius: "8px", backgroundColor: "var(--button-color)", boxShadow: "none" } },
          cancelButtonProps: { style: { height: "40px", borderRadius: "8px", boxShadow: "none" } },
        });
      });
    },
    [editPerformGradeConfig, editPerformList, getGradeConfig, getPerformList, gradeConfig?.id, t]
  );

  return {
    initialValues,
    onSubmitGradeConfig
  };
}

export default useBesmartcondition;
